import { useState, useCallback } from 'react';
import { doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../lib/firebase';
import type { ChatMessage } from '../types';

const TICKET_CHAT_COL = 'ticket_chats';

type TicketChat = {
  origin:      ChatMessage[];
  thread:      ChatMessage[];
  ticketTitle: string;
  createdAt:   string;
};

type UseTicketChatReturn = {
  chat:           TicketChat | null;
  loading:        boolean;
  loaded:         boolean;
  loadChat:       (notionId: string) => Promise<void>;
  appendMessages: (notionId: string, msgs: ChatMessage[]) => Promise<void>;
};

export function useTicketChat(): UseTicketChatReturn {
  const [chat, setChat]       = useState<TicketChat | null>(null);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded]   = useState(false);

  const loadChat = useCallback(async (notionId: string) => {
    if (loading || loaded) return;
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, TICKET_CHAT_COL, notionId));
      if (snap.exists()) {
        const data = snap.data() as Partial<TicketChat> & { messages?: ChatMessage[] };
        // Backward-compat: old docs had a single `messages` field
        const origin = data.origin ?? data.messages ?? [];
        setChat({
          origin:      origin,
          thread:      data.thread      ?? [],
          ticketTitle: data.ticketTitle ?? '',
          createdAt:   data.createdAt   ?? '',
        });
      } else {
        setChat(null);
      }
    } catch (err) {
      console.error('[useTicketChat] loadChat failed:', err);
      setChat(null);
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }, [loading, loaded]);

  const appendMessages = useCallback(async (notionId: string, msgs: ChatMessage[]) => {
    if (!msgs.length) return;
    // Optimistic update
    setChat(prev => ({
      origin:      prev?.origin      ?? [],
      thread:      [...(prev?.thread ?? []), ...msgs],
      ticketTitle: prev?.ticketTitle ?? '',
      createdAt:   prev?.createdAt   ?? '',
    }));
    const ref = doc(db, TICKET_CHAT_COL, notionId);
    try {
      await updateDoc(ref, {
        thread:    arrayUnion(...msgs),
        updatedAt: new Date().toISOString(),
      });
    } catch (err) {
      console.error('[useTicketChat] appendMessages failed:', err);
    }
  }, []);

  return { chat, loading, loaded, loadChat, appendMessages };
}
